import React from 'react';
import { Box, Flex, Text, Icon, CloseButton } from '@chakra-ui/react';
import { useToast } from '@chakra-ui/react';
import { motion } from 'framer-motion';
import { FaCheckCircle, FaExclamationCircle, FaInfoCircle, FaExclamationTriangle } from 'react-icons/fa';

const MotionBox = motion(Box);

const BRAND_BLUE = '#2569C9';

// Color map per status
const STATUS_CONFIG = {
  success: {
    icon: FaCheckCircle,
    color: BRAND_BLUE,
    bg: `${BRAND_BLUE}12`,
  },
  error: {
    icon: FaExclamationCircle,
    color: '#E53E3E',
    bg: '#FEE2E2',
  },
  warning: {
    icon: FaExclamationTriangle,
    color: '#F59E0B',
    bg: '#FEF3C7',
  },
  info: {
    icon: FaInfoCircle,
    color: BRAND_BLUE,
    bg: `${BRAND_BLUE}12`,
  },
};

// Custom toast card component
const HosteaToastCard = ({ status = 'success', title, description, onClose }) => {
  const config = STATUS_CONFIG[status] || STATUS_CONFIG.info;

  return (
    <MotionBox
      initial={{ opacity: 0, y: -20, scale: 0.95 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, y: -20, scale: 0.95 }}
      transition={{ duration: 0.3, ease: [0.25, 0.46, 0.45, 0.94] }}
      bg="white"
      rounded="2xl"
      border="1px solid"
      borderColor="gray.100"
      shadow="0 20px 40px -10px rgba(0, 0, 0, 0.15), 0 0 0 1px rgba(0, 0, 0, 0.03)"
      p={4}
      minW="320px"
      maxW="420px"
    >
      <Flex align="flex-start" gap={3}>
        {/* Icon circle */}
        <Flex
          w={10}
          h={10}
          borderRadius="full"
          bg={config.bg}
          align="center"
          justify="center"
          flexShrink={0}
        >
          <Icon as={config.icon} color={config.color} boxSize={4} />
        </Flex>

        {/* Text content */}
        <Box flex={1} pt={0.5}>
          {title && (
            <Text
              fontSize="sm"
              fontWeight="700"
              fontFamily="'General Sans', sans-serif"
              color="gray.900"
              lineHeight={1.4}
            >
              {title}
            </Text>
          )}
          {description && (
            <Text
              fontSize="xs"
              fontWeight="500"
              fontFamily="'General Sans', sans-serif"
              color="gray.500"
              lineHeight={1.5}
              mt={title ? 1 : 0}
            >
              {description}
            </Text>
          )}
        </Box>

        {/* Close button */}
        {onClose && (
          <CloseButton
            size="sm"
            onClick={onClose}
            color="gray.400"
            _hover={{ bg: 'gray.50', color: 'gray.700' }}
            flexShrink={0}
          />
        )}
      </Flex>
    </MotionBox>
  );
};

/**
 * Custom Hostea toast hook — same API as Chakra's useToast, but with a custom styled card.
 *
 * Usage:
 * const toast = useHosteaToast();
 * toast({ title: 'Success!', description: '...', status: 'success' });
 */
export const useHosteaToast = () => {
  const chakraToast = useToast();

  // Stable identity (chakraToast is already stable) so consumers can safely put `toast`
  // in useEffect/useCallback deps without triggering re-render loops.
  return React.useCallback((options = {}) => {
    const {
      title,
      description,
      status = 'info',
      duration = 4000,
      isClosable = true,
      position = 'bottom',
    } = options;

    // Pass ONLY render + duration + position. No other props.
    // Spreading extra props (like status/variant) can confuse Chakra into
    // rendering its default Alert instead of our custom component.
    return chakraToast({
      duration,
      position,
      isClosable: false,
      // Force the container to be transparent so Chakra's default Alert
      // background (if rendered as a fallback) doesn't leak through.
      containerStyle: {
        background: 'transparent',
        boxShadow: 'none',
        padding: 0,
        margin: 0,
      },
      render: ({ onClose }) => (
        <HosteaToastCard
          status={status}
          title={title}
          description={description}
          onClose={isClosable ? onClose : undefined}
        />
      ),
    });
  }, [chakraToast]);
};

export default useHosteaToast;
